import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import './AlertTrendChart.css';

const AlertTrendChart = ({ interval, apiBaseUrl }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAlertTrend();
  }, [interval]);

  const fetchAlertTrend = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${apiBaseUrl}/chat_safety/chat_alert_trends/${interval}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch alert trend data');
      }

      const result = await response.json();
      
      // Transform the data for the chart
      const transformedData = result.map(item => ({
        date: formatDate(item[0]),
        totalAlerts: item[1] || 0,
        confirmedAlerts: item[2] || 0,
      }));

      setData(transformedData);
    } catch (err) {
      console.error('Error fetching alert trend:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="alert-trend-tooltip">
          <p className="tooltip-date">{payload[0].payload.date}</p>
          <p className="tooltip-item total">
            <span className="tooltip-dot total"></span>
            Total Alerts: <strong>{payload[0].value}</strong>
          </p>
          <p className="tooltip-item confirmed">
            <span className="tooltip-dot confirmed"></span>
            Confirmed: <strong>{payload[1].value}</strong>
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="alert-trend-loading">
        <div className="spinner"></div>
        <span>Loading trend data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert-trend-error">
        <p>Error loading trend: {error}</p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="alert-trend-empty">
        <p>No trend data available for this period</p>
      </div>
    );
  }

  return (
    <div className="alert-trend-container">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis
            dataKey="date"
            stroke="#718096"
            style={{ fontSize: '12px' }}
            tickLine={false}
          />
          <YAxis
            stroke="#718096"
            style={{ fontSize: '12px' }}
            tickLine={false}
            allowDecimals={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: '13px', paddingTop: '10px' }}
            iconType="line"
          />
          <Line
            type="monotone"
            dataKey="totalAlerts"
            stroke="#667eea"
            strokeWidth={2}
            name="Total Alerts"
            dot={{ fill: '#667eea', r: 4 }}
            activeDot={{ r: 6 }}
          />
          <Line
            type="monotone"
            dataKey="confirmedAlerts"
            stroke="#e53e3e"
            strokeWidth={2}
            name="Confirmed Threats"
            dot={{ fill: '#e53e3e', r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AlertTrendChart;